import { registerLocaleData } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import { ENV_CONFIG } from '@sc/data-access-environment';
import { buildApiUrl, Environment, lang } from '@sc/data-access-models';
import { Observable, combineLatest, from } from 'rxjs';
import { map } from 'rxjs/operators';

const angularLocales: Record<lang, () => Promise<void>> = {
  fr_FR: () => {
    return import('@angular/common/locales/fr').then((locale) =>
      registerLocaleData(locale.default, 'fr-FR')
    );
  },
  es_ES: () => {
    return import('@angular/common/locales/es').then((locale) =>
      registerLocaleData(locale.default, 'es-ES')
    );
  },
  pt_BR: () => {
    return import('@angular/common/locales/pt').then((locale) =>
      registerLocaleData(locale.default, 'pt-BR')
    );
  },
  en_GB: () => {
    return import('@angular/common/locales/en').then((locale) =>
      registerLocaleData(locale.default, 'en-GB')
    );
  },
  en_US: () => {
    return import('@angular/common/locales/en').then((locale) =>
      registerLocaleData(locale.default, 'en-GB')
    );
  },
  et_EE: () => {
    return import('@angular/common/locales/et').then((locale) => {
      locale.default[11][0] = 'HH:mm';
      registerLocaleData(locale.default, 'et-EE');
    });
  },
  fi_FI: () => {
    return import('@angular/common/locales/fi').then((locale) => {
      locale.default[10][3] = 'd. MMMM y';
      registerLocaleData(locale.default, 'fi-FI');
    });
  },
  el_GR: () => {
    return import('@angular/common/locales/el').then((locale) =>
      registerLocaleData(locale.default, 'el-GR')
    );
  },
  it_IT: () => {
    return import('@angular/common/locales/it').then((locale) =>
      registerLocaleData(locale.default, 'it-IT')
    );
  },
  lt_LT: () => {
    return import('@angular/common/locales/lt').then((locale) =>
      registerLocaleData(locale.default, 'lt-LT')
    );
  },
  nb_NO: () => {
    return import('@angular/common/locales/nb').then((locale) =>
      registerLocaleData(locale.default, 'nb-NO')
    );
  },
  pt_PT: () => {
    return import('@angular/common/locales/pt').then((locale) =>
      registerLocaleData(locale.default, 'pt-PT')
    );
  },
  ru_RU: () => {
    return import('@angular/common/locales/ru').then((locale) =>
      registerLocaleData(locale.default, 'ru-RU')
    );
  },
  de_DE: () => {
    return import('@angular/common/locales/de').then((locale) =>
      registerLocaleData(locale.default, 'de-DE')
    );
  },
  hu_HU: () => {
    return import('@angular/common/locales/hu').then((locale) =>
      registerLocaleData(locale.default, 'hu-HU')
    );
  },
  nl_NL: () => {
    return import('@angular/common/locales/nl').then((locale) =>
      registerLocaleData(locale.default, 'nl_NL')
    );
  },
};

@Injectable({
  providedIn: 'root',
})
export class CustomLangLoaderService implements TranslateLoader {
  private readonly apiUrl: string;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly configService: ConfigurationService,
    @Inject(ENV_CONFIG) env: Environment
  ) {
    this.apiUrl = buildApiUrl(env.api, 'config/translation');
  }

  getTranslation(lang: string): Observable<any> {
    return combineLatest([
      this.httpClient.get(
        `${this.apiUrl}/${this.configService.getConfig().dictionary}/${lang}`
      ),
      from(angularLocales[lang as lang]()),
    ]).pipe(map(([dico, _]) => dico));
  }
}
